import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './Sidebar.module.css';
import { WebSettingsService } from '../../../services/WebSettingsService';
import { showSuccessToast, showErrorToast } from '../../Shared/Toastify/toastHelper';

import { ReactComponent as MainLogo } from '../../../images/Login/MainLogo.svg';
import { ReactComponent as DashboardIcon } from '../../../images/SideBar/Dashboard.svg';
import { ReactComponent as DashboardIconBold } from '../../../images/SideBar/DashboardB.svg';
import { ReactComponent as PublishedIcon } from '../../../images/SideBar/Publishers.svg';
import { ReactComponent as PublishedIconBold } from '../../../images/SideBar/PublishersB.svg';
import { ReactComponent as SchoolsIcon } from '../../../images/SideBar/Schools.svg';
import { ReactComponent as SchoolsIconBold } from '../../../images/SideBar/SchoolsB.svg';
import { ReactComponent as BooksIcon } from '../../../images/SideBar/Books.svg';
import { ReactComponent as BooksIconBold } from '../../../images/SideBar/BooksB.svg';
import { ReactComponent as UsersIcon } from '../../../images/SideBar/Users.svg';
import { ReactComponent as UsersIconBold } from '../../../images/SideBar/UsersB.svg';
import { ReactComponent as LogoutIcon } from '../../../images/SideBar/Logout.svg';
import { ReactComponent as CategoriesIcon } from '../../../images/SideBar/Categories.svg';
import { ReactComponent as CategoriesIconBold } from '../../../images/SideBar/CategoryIconBold.svg';
import { ReactComponent as GradesIcon } from '../../../images/SideBar/Grades.svg';
import { ReactComponent as GradesIconBold } from '../../../images/SideBar/GradesB.svg';
import { ReactComponent as AnnouncementsIcon } from '../../../images/SideBar/Announcements.svg';
import { ReactComponent as AnnouncementsIconBold } from '../../../images/SideBar/AnnouncementB.svg';
import { ReactComponent as ReportsIcon } from '../../../images/SideBar/Reports.svg';
import { ReactComponent as ReportsIconBold } from '../../../images/SideBar/ReportsB.svg';
import { ReactComponent as AdminAccountsIcon } from '../../../images/SideBar/Admin.svg';
import { ReactComponent as AdminAccountsIconBold } from '../../../images/SideBar/AdminAccountsB.svg';
import { ReactComponent as BatchIcon } from '../../../images/SideBar/BatchIcon.svg';
import { ReactComponent as BatchIconBold } from '../../../images/SideBar/BatchIconBold.svg';
import { ReactComponent as ContactUsIcon } from '../../../images/SideBar/ContactUsIcon.svg';
import { ReactComponent as ContactUsIconBold } from '../../../images/SideBar/contactusIconBold.svg';
import { ReactComponent as ContactDetailIcon } from '../../../images/ContactUsIcon.svg';
import { ReactComponent as ContactDetailsIconBold } from '../../../images/ContactUsIconBold.svg';
import { ReactComponent as DescriptionIcon } from '../../../images/DescriptionIcon.svg';
import { ReactComponent as DescriptionIconBold } from '../../../images/DesciprtionIconBold.svg';
import { ReactComponent as FAQIcon } from '../../../images/FAQIcon.svg';
import { ReactComponent as FAQIconBold } from '../../../images/FAQIconBold.svg';
import { ReactComponent as PageIconBold } from '../../../images/pageIconBold.svg';
import { ReactComponent as PageIcon } from '../../../images/PageIcon.svg';
import { UserContext } from '../../../UserContext';
import CustomToggleButton from '../CustomToggleButton';
import { useWebSettings } from '../../../context/WebSettingsContext';

interface SidebarProps {
  role: 'admin' | 'editor' | 'sales' | '';
}

const Sidebar: React.FC<SidebarProps> = ({ role }) => {
  const location = useLocation();
  const [activeItem, setActiveItem] = useState<string | null>('dashboard');
  const [topPurchasedBooksEnabled, setTopPurchasedBooksEnabled] = useState(false);
  const { logOut } = useContext(UserContext);
  const { webSettings } = useWebSettings();

  useEffect(() => {
    const pathToActiveItem: Record<string, string> = {
      'publishers': 'published',
      'schools': 'schools',
      'dashboard': 'dashboard',
      'books': 'books',
      'batches': 'batches',
      'users': 'users',
      'categories': 'categories',
      'grades': 'grades',
      'contactus': 'contactus',
      'announcements': 'announcements',
      'reports-website': 'reports-website',
      'reports-mobile': 'reports-mobile',
      'adminaccounts': 'adminaccounts',
      'accesscodes': 'batches',
      "transactions":"transactions",
      "contactDetails": "contactDetails",
      "description": "description",
      "banner-slides": "banner-slides",
      "faq":"faq",
      "terms":"terms",
      'privacy':'privacy',
      'help':'help'
    };

    const basePath = location.pathname.split('/')[1];

    setActiveItem(pathToActiveItem[basePath] || "dashboard");
  }, [location.pathname]);

  useEffect(() => {
    const fetchWebSettings = async () => {
      try {
        const webSettings = await WebSettingsService.getWebSettings();
        console.log(webSettings)
        setTopPurchasedBooksEnabled(webSettings.webSettings.topPurchasedBookSection);
      } catch (error) {
        console.error('Error fetching web settings:', error);
      }
    };
    fetchWebSettings();
  }, []);

  const handleMenuItemClick = (item: string) => {
    setActiveItem(item);
  };

  const navigate = useNavigate();
  const handleLogout = async () => {
    navigate("/");
    await logOut()
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.logo}>
        <MainLogo />
      </div>
      {role === 'admin' || role === 'editor' ? (
        <>
          <div className={styles.sectionTitle}>MAIN MENU</div>

          <Link
            to="/dashboard"
            className={`${styles.menuItem} ${activeItem === 'dashboard' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('dashboard')}
          >
            {activeItem === 'dashboard' ? <DashboardIconBold className={styles.icon} /> : <DashboardIcon className={styles.icon} />}
            Dashboard
          </Link>

          <Link
            to="/publishers"
            className={`${styles.menuItem} ${activeItem === 'published' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('published')}
          >
            {activeItem === 'published' ? <PublishedIconBold className={styles.icon} /> : <PublishedIcon className={styles.icon} />}
            Publishers
          </Link>

          <Link
            to="/schools"
            className={`${styles.menuItem} ${activeItem === 'schools' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('schools')}
          >
            {activeItem === 'schools' ? <SchoolsIconBold className={styles.icon} /> : <SchoolsIcon className={styles.icon} />}
            Institution
          </Link>

          <Link
            to="/books"
            className={`${styles.menuItem} ${activeItem === 'books' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('books')}
          >
            {activeItem === 'books' ? <BooksIconBold className={styles.icon} /> : <BooksIcon className={styles.icon} />}
            Books
          </Link>

          <Link
            to="/batches"
            className={`${styles.menuItem} ${activeItem === 'batches' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('batches')}
          >
            {activeItem === 'batches' ? <BatchIconBold className={styles.icon} /> : <BatchIcon className={styles.icon} />}
            Batches
          </Link>

          <Link
            to="/users"
            className={`${styles.menuItem} ${activeItem === 'users' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('users')}
          >
            {activeItem === 'users' ? <UsersIconBold className={styles.icon} /> : <UsersIcon className={styles.icon} />}
            Users
          </Link>

          <div className={styles.sectionTitle}>FILTERS</div>

          <Link
            to="/categories"
            className={`${styles.menuItem} ${activeItem === 'categories' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('categories')}
          >
            {activeItem === 'categories' ? <CategoriesIconBold className={styles.icon} /> : <CategoriesIcon className={styles.icon} />}
            Categories
          </Link>

          <Link
            to="/grades"
            className={`${styles.menuItem} ${activeItem === 'grades' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('grades')}
          >
            {activeItem === 'grades' ? <GradesIconBold className={styles.icon} /> : <GradesIcon className={styles.icon} />}
            Grades
          </Link>

          <div className={styles.sectionTitle}>INFORMATION</div>
          <Link
            to="/announcements"
            className={`${styles.menuItem} ${activeItem === 'announcements' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('announcements')}
          >
            {activeItem === 'announcements' ? <AnnouncementsIconBold className={styles.icon} /> : <AnnouncementsIcon className={styles.icon} />}
            Announcements
          </Link>

          {role === "admin" &&
            <>
            <Link
                to="/transactions"
                className={`${styles.menuItem} ${activeItem === 'transactions' ? styles.active : ''}`}
                onClick={() => handleMenuItemClick('transactions')}
              >
                {activeItem === 'transactions' ? <BooksIconBold className={styles.icon} /> : <BooksIcon className={styles.icon} />}
                    Transactions
              </Link>
            
              <Link
                to="/reports-website"
                className={`${styles.menuItem} ${activeItem === 'reports-website' ? styles.active : ''}`}
                onClick={() => handleMenuItemClick('reports-website')}
              >
                {activeItem === 'reports-website' ? <ReportsIconBold className={styles.icon} /> : <ReportsIcon className={styles.icon} />}
                Reports - Website
              </Link>
              <Link
                to="/reports-mobile"
                className={`${styles.menuItem} ${activeItem === 'reports-mobile' ? styles.active : ''}`}
                onClick={() => handleMenuItemClick('reports-mobile')}
              >
                {activeItem === 'reports-mobile' ? <ReportsIconBold className={styles.icon} /> : <ReportsIcon className={styles.icon} />}
                Reports - Mobile
              </Link>
            </>
          }

          <div className={styles.sectionTitle}>WEBSITE MANAGEMENT</div>
          <Link
            to="/banner-slides"
            className={`${styles.menuItem} ${activeItem === 'banner-slides' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('banner-slides')}
          >
            {activeItem === 'banner-slides' ? <AnnouncementsIconBold className={styles.icon} /> : <AnnouncementsIcon className={styles.icon} />}
            Banner Slides
          </Link>

          <Link
            to="/description"
            className={`${styles.menuItem} ${activeItem === 'description' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('description')}
          >
            {activeItem === 'description' ? <DescriptionIconBold className={styles.icon} /> : <DescriptionIcon className={styles.icon} />}
            Description
          </Link>
          <Link
            to="/faq"
            className={`${styles.menuItem} ${activeItem === 'faq' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('faq')}
          >
            {activeItem === 'faq' ? <FAQIconBold className={styles.icon} /> : <FAQIcon className={styles.icon} />}
            FAQs
          </Link>

          <Link
            to="/terms"
            className={`${styles.menuItem} ${activeItem === 'terms' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('terms')}
          >
            {activeItem === 'terms' ? <PageIconBold className={styles.icon} /> : <PageIcon className={styles.icon} />}
            Terms & Conditions
          </Link>
          
          <Link
            to="/privacy"
            className={`${styles.menuItem} ${activeItem === 'privacy' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('privacy')}
          >
            {activeItem === 'privacy' ? <PageIconBold className={styles.icon} /> : <PageIcon className={styles.icon} />}
            Privacy Policy
          </Link>

          <Link
            to="/help"
            className={`${styles.menuItem} ${activeItem === 'help' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('help')}
          >
            {activeItem === 'help' ? <PageIconBold className={styles.icon} /> : <PageIcon className={styles.icon} />}
            Help & Support
          </Link>


          <Link
            to="/contactDetails"
            className={`${styles.menuItem} ${activeItem === 'contactDetails' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('contactDetails')}
          >
            {activeItem === 'contactDetails' ? <ContactDetailsIconBold className={styles.icon} /> : <ContactDetailIcon className={styles.icon} />}
            Contact Details
          </Link>

          <Link
            to="/contactus"
            className={`${styles.menuItem} ${activeItem === 'contactus' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('contactus')}
            >
            {activeItem === 'contactus' ? <ContactUsIconBold className={styles.icon} /> : <ContactUsIcon className={styles.icon} />}
            Contact Us
            </Link>

          <div
            className={`${styles.menuItem} ${activeItem === 'announcements' ? styles.active : ''}`}
          >
            <AnnouncementsIcon className={styles.icon} />
            Top Purchased Books
            <CustomToggleButton 
              onChange={async (enabled) => {
                try {
                  await WebSettingsService.updateTopPurchasedBooks(enabled, webSettings._id);
                  setTopPurchasedBooksEnabled(enabled);
                  showSuccessToast('Top purchased books section updated successfully');
                } catch (error: any) {
                  showErrorToast('Error updating top purchased books section: ' + error.message);
                }
              }}
              isOn={topPurchasedBooksEnabled}
            />
          </div>

          {role === 'admin' && (
            <>

              <div className={styles.sectionTitle}>ADMIN</div>

              <Link
                to="/adminaccounts"
                className={`${styles.menuItem} ${activeItem === 'adminaccounts' ? styles.active : ''}`}
                onClick={() => handleMenuItemClick('adminaccounts')}
              >
                {activeItem === 'adminaccounts' ? <AdminAccountsIconBold className={styles.icon} /> : <AdminAccountsIcon className={styles.icon} />}
                Admin Accounts
              </Link>
            </>
          )}
        </>
      ) : role === 'sales' ? (
        <>
          <div className={styles.sectionTitle}>MAIN MENU</div>
          <Link
            to="/reports-mobile"
            className={`${styles.menuItem} ${activeItem === 'reports-mobile' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('reports-mobile')}
          >
            {activeItem === 'reports-mobile' ? <ReportsIconBold className={styles.icon} /> : <ReportsIcon className={styles.icon} />}
            Reports - Mobile
          </Link>
          <Link
            to="/reports-website"
            className={`${styles.menuItem} ${activeItem === 'reports-website' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('reports-website')}
          >
            {activeItem === 'reports-website' ? <ReportsIconBold className={styles.icon} /> : <ReportsIcon className={styles.icon} />}
            Reports - Website
          </Link>
          <Link
            to="/users"
            className={`${styles.menuItem} ${activeItem === 'users' ? styles.active : ''}`}
            onClick={() => handleMenuItemClick('users')}
          >
            {activeItem === 'users' ? <UsersIconBold className={styles.icon} /> : <UsersIcon className={styles.icon} />}
            Users
          </Link>
        </>
      ) : null}


      <div className={`${styles.menuItem} ${styles.logout}`} onClick={handleLogout}>
        <LogoutIcon className={styles.icon} /> Logout
      </div>
    </div>
  );
};

export default Sidebar;
